import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer, API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Search, Phone, ArrowRight, Navigation, Building2 } from "lucide-react";
import axios from "axios";

// Dealership photos are original uploads on Sanity's CDN (often 5000px+, several
// MB each). Request a card-sized rendition from Sanity's image pipeline instead —
// far less Sanity bandwidth, and a much faster page.
const sizedSanityImage = (url) =>
  url && url.startsWith("https://cdn.sanity.io/images/") && !url.includes("?")
    ? `${url}?w=800&fit=max&auto=format`
    : url;

const DealerCard = ({ dealer, isClosest, onSelect }) => {
  const navigate = useNavigate();
  return (
    <div
      className={`surface-card overflow-hidden transition-all ${isClosest ? "ring-2 ring-slate-800/20 shadow-md" : "hover:shadow-md"}`}
      data-testid={`dealer-card-${dealer.id}`}
    >
      {/* Image */}
      <div className="aspect-[16/9] overflow-hidden bg-slate-100 relative">
        <img
          src={sizedSanityImage(dealer.image)}
          alt={dealer.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {isClosest && (
          <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: "hsl(220, 70%, 15%)" }}>
            Closest Location
          </div>
        )}
        {dealer.distance_miles !== null && (
          <div className="absolute top-3 right-3 px-2.5 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-semibold text-slate-700 flex items-center gap-1">
            <Navigation className="w-3 h-3" />
            {dealer.distance_miles} mi
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-5">
        <div className="flex items-start gap-1 mb-1">
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
            {dealer.franchise}
          </span>
        </div>
        <h3 className="font-semibold text-slate-900 mt-2">{dealer.name}</h3>
        <div className="flex items-start gap-2 mt-2 text-sm text-slate-500">
          <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{dealer.address}, {dealer.city}, {dealer.state} {dealer.zip}</span>
        </div>

        {/* CTA */}
        <Button
          onClick={() => navigate("/")}
          className="w-full mt-4 h-11 btn-accent text-sm"
          data-testid={`explore-options-${dealer.id}`}
        >
          Explore My Options
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

const LocationsScreen = () => {
  const [zipCode, setZipCode] = useState("");
  const [searchedZip, setSearchedZip] = useState("");
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("1-800-781-NUCAR");
  const [total, setTotal] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const inputRef = useRef(null);

  const fetchDealers = async (zip = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (zip) params.set("zip_code", zip);
      params.set("limit", showAll ? "50" : "12");
      const res = await axios.get(`${API}/dealerships?${params}`);
      setDealers(res.data.dealerships);
      setPhone(res.data.phone);
      setTotal(res.data.total);
      setSearchedZip(zip);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to fetch dealerships:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDealers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (zipCode.length === 5 && /^\d{5}$/.test(zipCode)) {
      fetchDealers(zipCode);
    }
  };

  const handleClear = () => {
    setZipCode("");
    setSearchedZip("");
    fetchDealers();
    inputRef.current?.focus();
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header variant="dark" />

      {/* Hero */}
      <section className="header-nucar py-16 sm:py-20">
        <div className="container-wide text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Find Your Nearest <span className="text-accent-yellow">Nucar</span> Location
          </h1>
          <p className="text-slate-300 mt-3 text-base sm:text-lg max-w-xl mx-auto">
            Enter your ZIP code to find the closest dealership for vehicle drop-off and inspection.
          </p>

          {/* ZIP Search */}
          <form onSubmit={handleSearch} className="mt-8 max-w-md mx-auto">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  placeholder="Enter ZIP code"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value.replace(/\D/g, ""))}
                  className="pl-11 h-14 rounded-xl bg-white text-lg"
                  data-testid="location-zip-input"
                />
              </div>
              <Button
                type="submit"
                disabled={zipCode.length !== 5}
                className="h-14 px-6 btn-accent rounded-xl text-base disabled:opacity-50"
                data-testid="location-search-btn"
              >
                Search
              </Button>
            </div>
          </form>

          {/* Centralized phone */}
          <div className="mt-6 flex items-center justify-center gap-2 text-slate-300 text-sm">
            <Phone className="w-4 h-4" />
            <span>Call our buy-center team: <strong className="text-white">{phone}</strong></span>
          </div>
        </div>
      </section>

      {/* Results */}
      <main className="py-12 sm:py-16">
        <div className="container-wide">
          {/* Results header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              {searchedZip ? (
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">
                    Locations near <span className="font-mono">{searchedZip}</span>
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Showing {dealers.length} of {total} locations, sorted by distance
                  </p>
                </div>
              ) : (
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">All Locations</h2>
                  <p className="text-sm text-slate-500 mt-1">{total} dealerships across the Northeast</p>
                </div>
              )}
            </div>
            {searchedZip && (
              <button onClick={handleClear} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-testid="clear-search-btn">
                Show all locations
              </button>
            )}
          </div>

          {/* Dealer Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="surface-card overflow-hidden">
                  <div className="aspect-[16/9] skeleton" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 w-20 skeleton rounded" />
                    <div className="h-5 w-48 skeleton rounded" />
                    <div className="h-4 w-56 skeleton rounded" />
                    <div className="h-11 skeleton rounded-xl" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {dealers.map((dealer, i) => (
                  <DealerCard
                    key={dealer.id}
                    dealer={dealer}
                    isClosest={searchedZip && i === 0}
                  />
                ))}
              </div>

              {!showAll && dealers.length < total && (
                <div className="text-center mt-10">
                  <Button
                    variant="outline"
                    onClick={() => { setShowAll(true); fetchDealers(searchedZip); }}
                    className="btn-secondary"
                    data-testid="show-all-locations-btn"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    View All {total} Locations
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Contact CTA */}
          <div className="mt-16 text-center p-8 sm:p-12 rounded-2xl" style={{ backgroundColor: "hsl(220, 70%, 15%)" }}>
            <h2 className="text-2xl font-semibold text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              Ready to get started?
            </h2>
            <p className="text-slate-300 mb-6">
              Get your instant cash offer in seconds — no appointment needed to start.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => window.location.href = "/"}
                className="h-14 px-8 btn-accent text-base"
                data-testid="explore-options-cta"
              >
                Explore My Options
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <div className="flex items-center justify-center gap-2 text-slate-300">
                <Phone className="w-5 h-5" />
                <span className="text-lg font-medium text-white">{phone}</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LocationsScreen;
